import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  makeStyles,
} from "@material-ui/core";
import React, { FC, useState } from "react";
import { useField, useForm } from "react-final-form";
import { Button } from "react-admin";
import { useSelector } from "react-redux";
import { AppState } from "../../../components/layout/Menu";
import Stack from "components/Stack";
import { isAssignedSubcontractorRequiredValidation } from "utils/validation";

const useStyles = makeStyles({
  dialog: {
    "& .MuiDialog-paper": {
      overflowY: "unset",
    },
  },
});

interface StepperNavigationProps {
  activeStep: number;
  handleNext: () => void;
  handleBack: () => void;
  mode: "create" | "update";
}

const StepperNavigation: FC<StepperNavigationProps> = ({
  activeStep,
  handleNext,
  handleBack,
  mode,
}) => {
  const [open, setOpen] = useState<boolean>(false);
  const [actionDirection, setActionDirection] = useState<
    "back" | "next" | null
  >(null);

  const selectedRouteState = useSelector(
    (state: AppState) => state.navMode.route,
  );

  const form = useForm();

  const formRoutesChangedField = useField("formRoutesChanged");
  const droppedLocationsField = useField("droppedLocations");
  const studentsField = useField("students");
  const busScheduleIdField = useField("busScheduleId");
  const desiredRouteDurationField = useField("desiredRouteDuration");
  const maxVehicleCapacityField = useField("maxVehicleCapacity");
  const maxStopTimeField = useField("maxStopTime");
  const routesField = useField("routes");

  const classes = useStyles();

  const routesWithoutAssignedSubcontractors = routesField.input.value.filter(
    (route: any) => !isAssignedSubcontractorRequiredValidation(route),
  );

  const handleOpen = (direction: "back" | "next") => {
    setActionDirection(direction);
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  // Conditions for showing dialog
  const shouldWarnOnNext = (step: number): boolean => {
    if (
      mode === "create" &&
      step === 2 &&
      (droppedLocationsField.input.value.length > 0 ||
        routesWithoutAssignedSubcontractors.length > 0)
    )
      return true;

    if (
      mode === "update" &&
      step === 1 &&
      routesWithoutAssignedSubcontractors.length > 0
    )
      return true;

    return false;
  };

  const shouldWarnOnBack = (step: number): boolean => {
    if (
      mode === "create" &&
      step === 2 &&
      formRoutesChangedField.input.value === true
    )
      return true;

    return false;
  };

  const handleBackStepper = () => {
    if (shouldWarnOnBack(activeStep)) {
      handleOpen("back");
    } else {
      handleBack();
    }
  };

  const handleNextStepper = () => {
    if (shouldWarnOnNext(activeStep)) {
      handleOpen("next");
    } else {
      if (mode === "create" && activeStep === 3) {
        form.submit();
        return;
      }

      if (mode === "update" && activeStep === 2) {
        form.submit();
        return;
      }

      handleNext();
    }
  };

  const warningMessagesCreate: any = {
    back: {
      0: "",
      1: "",
      2: "You have unsaved changes. Are you sure you want to go back?",
      3: "",
    },
    next: {
      0: "",
      1: "",
      2: `${
        droppedLocationsField.input.value.length > 0
          ? `Some locations <strong>aren't assigned</strong> to any route yet. <br/>`
          : ""
      } ${
        routesWithoutAssignedSubcontractors.length > 0
          ? `You have <strong>${routesWithoutAssignedSubcontractors.length} routes</strong> without assigned subcontractors which is <strong>required.</strong><br />`
          : ""
      } Please assign them or remove them before proceeding. `,
      3: "",
    },
  };

  const warningMessagesUpdate: any = {
    back: {
      0: "",
      1: "You have unsaved changes. Are you sure you want to go back?",
      2: "",
    },
    next: {
      0: "",
      1: `${
        routesWithoutAssignedSubcontractors.length > 0
          ? `You have <strong>${routesWithoutAssignedSubcontractors.length} routes</strong> without assigned subcontractors which is <strong>required.</strong><br />`
          : ""
      } Please assign them or remove them before proceeding. `,
      2: "",
    },
  };

  const getWarningMessage = () => {
    if (!actionDirection) return "";

    let message;

    if (mode === "create") {
      message = warningMessagesCreate[actionDirection][activeStep] || "";
    }

    if (mode === "update") {
      message = warningMessagesUpdate[actionDirection][activeStep] || "";
    }

    return (
      <div
        dangerouslySetInnerHTML={{ __html: message }}
        style={{ color: "initial" }}
      />
    );
  };

  const isNextDisabled = () => {
    if (mode === "create") {
      switch (activeStep) {
        case 0:
          if (!busScheduleIdField.input.value) {
            return true;
          }

          if (!desiredRouteDurationField.input.value) {
            return true;
          }

          if (!maxVehicleCapacityField.input.value) {
            return true;
          }

          if (!maxStopTimeField.input.value) {
            return true;
          }

          return false;
        case 1:
          return !(studentsField.input.value.length > 0);
        default:
          return false;
      }
    }

    if (mode === "update") {
      switch (activeStep) {
        default:
          return false;
      }
    }

    return false;
  };

  return (
    <div
      style={{
        padding: "12px 14px",
      }}
    >
      {selectedRouteState ? (
        <></>
      ) : (
        <>
          <Stack direction="row" spacing={2} justifyContent="space-between">
            <Button
              disabled={activeStep === 0}
              onClick={handleBackStepper}
              label="Previous"
            />
            <Button
              onClick={handleNextStepper}
              disabled={isNextDisabled()}
              label={
                (mode === "create" && activeStep === 3) ||
                (mode === "update" && activeStep === 2)
                  ? "Finish"
                  : "Next"
              }
            />
          </Stack>

          <Divider style={{ marginTop: "24px" }} />

          <Dialog
            open={open}
            aria-labelledby="alert-dialog-title"
            aria-describedby="alert-dialog-description"
            className={classes.dialog}
            maxWidth="md"
          >
            <DialogTitle id="alert-dialog-title">Warning</DialogTitle>
            <DialogContent>{getWarningMessage()}</DialogContent>
            <DialogActions>
              {(mode === "create" &&
                activeStep === 2 &&
                actionDirection === "next") ||
              (mode === "update" &&
                activeStep === 1 &&
                actionDirection === "next") ? (
                <Button
                  onClick={handleClose}
                  color="secondary"
                  label="Understand"
                />
              ) : (
                <>
                  <Button
                    onClick={handleClose}
                    color="secondary"
                    label="Cancel"
                  />
                  <Button
                    onClick={() => {
                      handleBack();
                      handleClose();
                    }}
                    color="primary"
                    label="Proceed"
                  />
                </>
              )}
            </DialogActions>
          </Dialog>
        </>
      )}
    </div>
  );
};

export default StepperNavigation;
